<template>
  <Teleport to="body">
    <div v-show="isVisible" class="task-autocomplete" :style="dropdownStyle">
      <div
        v-for="(task, index) in tasksList"
        :key="task.id"
        :class="['task-item', { active: index === selectedIdx }]"
        @click="$emit('select', task)"
        @mouseenter="$emit('hover', index)"
      >
        <div class="task-icon">📋</div>
        <div class="task-info">
          <div class="task-id">#{{ extractTaskIid(task) }}</div>
          <div class="task-title">{{ task.title }}</div>
          <div v-if="task.workflowStatus" class="task-status">
            <span
              class="status-badge"
              :style="{
                backgroundColor: task.workflowStatus.color || '#6B7280',
                color: getContrastTextColor(task.workflowStatus.color || '#6B7280'),
              }"
            >
              {{ task.workflowStatus.name }}
            </span>
          </div>
        </div>
      </div>
      <div v-if="tasksLoading" class="task-loading">
        {{ __('Loading…') }}
      </div>
      <div v-if="!tasksLoading && tasksList.length === 0 && mentionQuery" class="task-empty">
        {{ s__('TaskMention|No tasks found') }}
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { __, s__ } from '@/locale'
import { getContrastTextColor } from '@/utils/colorContrast.js'

const props = defineProps({
  show: { type: Boolean, default: false },
  position: { type: Object, default: () => ({ x: 0, y: 0 }) },
  filteredTasks: { type: Array, default: () => [] },
  selectedIndex: { type: Number, default: 0 },
  tasksLoading: { type: Boolean, default: false },
  mentionQuery: { type: String, default: '' },
})

defineEmits(['select', 'hover'])

const extractTaskIid = task => {
  if (!task) return ''
  return task.iid || ''
}

const isVisible = computed(() => {
  const showValue =
    typeof props.show === 'object' && props.show?.value !== undefined
      ? props.show.value
      : props.show
  const tasks = Array.isArray(props.filteredTasks)
    ? props.filteredTasks
    : props.filteredTasks?.value || []
  return showValue && (tasks.length > 0 || props.tasksLoading)
})

const tasksList = computed(() => {
  const tasks = Array.isArray(props.filteredTasks)
    ? props.filteredTasks
    : props.filteredTasks?.value || []
  return tasks.filter(t => t && t.id)
})

const selectedIdx = computed(() => {
  return typeof props.selectedIndex === 'object' && props.selectedIndex?.value !== undefined
    ? props.selectedIndex.value
    : props.selectedIndex
})

const positionValue = computed(() => {
  if (typeof props.position === 'object' && props.position?.value) {
    return props.position.value
  }
  return props.position || { x: 0, y: 0 }
})

// Compute style based on whether we're positioning above or below
const dropdownStyle = computed(() => {
  const pos = positionValue.value
  if (pos.positionAbove) {
    // Position above: anchor bottom edge to the y coordinate
    return {
      left: `${pos.x}px`,
      bottom: `${window.innerHeight - pos.y}px`,
      top: 'auto',
    }
  } else {
    // Position below: anchor top edge to the y coordinate
    return {
      left: `${pos.x}px`,
      top: `${pos.y}px`,
      bottom: 'auto',
    }
  }
})
</script>

<style scoped>
.task-autocomplete {
  position: fixed;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 4px 0;
  min-width: 250px;
  max-width: 350px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 10001;
  font-size: 14px;
}

.task-item {
  display: flex;
  align-items: flex-start;
  padding: 8px 12px;
  cursor: pointer;
  gap: 10px;
  transition: background 0.15s;
}

.task-item:hover,
.task-item.active {
  background: #f0f0f0;
}

.task-icon {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.task-info {
  flex: 1;
  min-width: 0;
}

.task-id {
  font-weight: 600;
  color: #1d6bf3;
  font-size: 13px;
  margin-bottom: 2px;
}

.task-title {
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.task-status {
  margin-top: 2px;
}

.status-badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.task-loading,
.task-empty {
  padding: 12px;
  text-align: center;
  color: #666;
  font-size: 13px;
}
</style>
